import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { POST_LOGIN_REDIRECT_KEY } from "./usePostLoginRedirect";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const {
    darkModeLogo,
    lightModeLogo,
    title,
    disableEmailPasswordAuthentication,
  } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const isOAuthCallback = window.location.hash.includes("access_token=");
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  // The OAuth consent page (and any protected route) sends unauthenticated
  // users here with a `?redirect=` target. Honor it so they land back where
  // they intended after signing in, rather than on the dashboard.
  const redirectAfterLogin =
    new URLSearchParams(location.search).get("redirect") ?? redirectTo;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectAfterLogin)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // Google login is a full-page round-trip, so the `?redirect=` target on the
    // URL won't survive it. Stash it for usePostLoginRedirect() to restore once
    // the user returns authenticated.
    if (redirectAfterLogin) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirectAfterLogin);
    }
    login({ provider: "google" }).catch((error) => {
      setGoogleLoading(false);
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "error",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );
    });
  };

  const anyLoading = loading || googleLoading;

  if (isOAuthCallback) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <svg
          aria-hidden="true"
          className="size-6 animate-spin text-nd-text-disabled"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      </div>
    );
  }

  return (
    // min-h-dvh: accounts for mobile browser chrome (address bar); 100vh does not
    <div className="min-h-dvh flex">
      <div className="relative grid w-full lg:grid-cols-2">
        {/* ── Left: Brand panel (desktop only) ─────────────────────── */}
        <div
          className="relative hidden h-full flex-col p-10 lg:flex"
          style={{
            // hex fallback for browsers without oklch support
            background: "#171512",
            // @ts-expect-error — valid CSS, TS doesn't know about oklch
            "--bg": "oklch(0.09 0.003 55)",
          }}
        >
          {/* Logo mark — top left */}
          <div className="relative z-10">
            <img className="h-7" src={darkModeLogo} alt={title} />
          </div>

          {/* Brand statement — anchored to bottom */}
          <div className="flex-1 flex flex-col justify-end">
            {/* Orange accent line */}
            <div
              className="mb-7"
              style={{
                width: "2rem",
                height: "2px",
                background: "var(--nd-orange)",
              }}
            />

            {/* Title at dramatic scale — overflow-wrap prevents long titles from escaping */}
            <h2
              className="font-nd-grotesk font-bold text-white leading-[1.02] tracking-tight overflow-hidden"
              style={{
                fontSize: "clamp(2.5rem, 3.8vw, 3.75rem)",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
              }}
            >
              {title}
            </h2>

            {/* Minimal footer label */}
            <p
              className="mt-8 font-nd-mono text-[10px] tracking-[0.18em] uppercase"
              style={{ color: "oklch(0.38 0.004 55)" }}
            >
              Internal access
            </p>
          </div>
        </div>

        {/* ── Right: Form panel ────────────────────────────────────── */}
        <div className="flex flex-col justify-center w-full px-6 py-12 lg:px-14">
          {/* Mobile-only logo (left panel is hidden on small screens) */}
          <div className="flex items-center gap-2 mb-10 lg:hidden">
            <img className="h-6 dark:hidden" src={lightModeLogo} alt={title} />
            <img
              className="h-6 hidden dark:block"
              src={darkModeLogo}
              alt={title}
            />
          </div>

          <div className="w-full max-w-[360px] mx-auto space-y-8">
            {/* Heading */}
            <div className="space-y-1">
              <p className="font-nd-mono text-[10px] tracking-[0.18em] uppercase text-nd-text-disabled">
                {translate("crm.auth.access", { _: "Account access" })}
              </p>
              <h1 className="font-nd-grotesk text-3xl font-bold tracking-tight text-nd-text-display">
                {translate("ra.auth.sign_in")}
              </h1>
            </div>

            {/* Email + password form */}
            {disableEmailPasswordAuthentication ? null : (
              <Form className="space-y-6" onSubmit={handleSubmit}>
                <TextInput
                  label="ra.auth.email"
                  source="email"
                  type="email"
                  validate={required()}
                />
                <TextInput
                  label="ra.auth.password"
                  source="password"
                  type="password"
                  validate={required()}
                />
                <div className="flex flex-col gap-3 pt-1">
                  <Button
                    type="submit"
                    className="w-full cursor-pointer"
                    disabled={anyLoading}
                  >
                    {loading
                      ? translate("crm.auth.signing_in", {
                          _: "Signing in…",
                        })
                      : translate("ra.auth.sign_in")}
                  </Button>
                </div>
              </Form>
            )}

            {/* Google SSO */}
            <Button
              type="button"
              variant="outline"
              className="w-full cursor-pointer gap-2.5"
              disabled={anyLoading}
              onClick={handleGoogleLogin}
            >
              {googleLoading ? (
                <svg
                  aria-hidden="true"
                  className="size-4 shrink-0 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="size-4 shrink-0"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              )}
              {googleLoading
                ? translate("crm.auth.signing_in", { _: "Signing in…" })
                : "Sign in with Google"}
            </Button>

            {/* Forgot password */}
            {disableEmailPasswordAuthentication ? null : (
              <Link
                to={"/forgot-password"}
                className="block font-nd-mono text-[10px] tracking-[0.08em] uppercase text-center text-nd-text-disabled hover:text-nd-text-secondary transition-colors"
              >
                {translate("ra-supabase.auth.forgot_password", {
                  _: "Forgot password?",
                })}
              </Link>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
